import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, MessageCircleIcon, ShieldCheckIcon, TruckIcon } from 'lucide-react';
import { founder, salon } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { euro, rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { ease } from '../../core/format/motion';
import { activePromo } from '../../core/rules/promo';
import { canSubscribe } from '../../core/rules/features';
import { useFeatures } from '../../hooks/useFeatures';
import type { Product } from '../../core/types/catalog';
import { btnPrice, btnPriceAmount, btnSecondary } from '../../ui/styles';
// Sterren volgen het echte gemiddelde: een 4,2 toont vier volle sterren, niet vijf.
import { Stars } from '../shared/Stars';
import { PurchaseOptions } from '../shared/PurchaseOptions';
import { oneOffMeta, refillMeta, soloRefillPlan } from './productRules';
type Props = {
  product: Product;
  soldOut: boolean;
  subscribe: boolean;
  onSubscribe: (value: boolean) => void;
  added: boolean;
  onAdd: () => void;
  /** Na toevoegen: de mand openen (de volgende logische stap). */
  onViewCart: () => void;
  giftHint: string | null;
  /** Hieronder: de uitklapblokken met waarom, gebruik en ingrediënten. */
  children: ReactNode;
};

/** Merk, naam, beoordeling, eenmalig of bijvullen, en de knop. */
export function ProductBuyBox({
  product,
  soldOut,
  subscribe,
  onSubscribe,
  added,
  onAdd,
  onViewCart,
  giftHint,
  children
}: Props) {
  const pages = usePages();
  const reduce = useReducedMotion();
  const features = useFeatures();
  const {
    policy
  } = useSite();
  const promo = activePromo(product);
  // Slim bijvullen met alleen dit product: haalt een doos het minimum? Zo niet, geen keuze maar een zachte tip.
  const plan = canSubscribe(features, product) ? soloRefillPlan(product, policy.subscription) : null;
  const options = [{
    value: false,
    title: 'Eenmalig',
    meta: oneOffMeta(product)
  }, ...(plan?.ok ? [{
    value: true,
    title: `Bijvullen wanneer het op is · ${pages.subscription.name}`,
    meta: refillMeta(plan, policyCopy.subscriptionPerks)
  }] : [])];
  return <div className="lg:sticky lg:top-28" data-id="element-8102" data-mp="1cxyz5vvpjkj6">
      <p className="text-[14px] text-clay" data-id="element-8103" data-mp="1d0cve824j2sb">{product.brand}</p>
      <h1 className="mt-2 type-sub text-ink" data-id="element-8104" data-mp="1ruhgzzybd2r9">{product.name}</h1>
      {product.reviews > 0 && <a href="#reviews" className="mt-4 inline-flex items-center gap-2 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-8105" data-mp="1mvd8lbj7r91z">
          <Stars value={product.rating} data-id="element-8106" data-mp="1wm7swacwfyks" />
          <span className="sr-only" data-id="element-8107" data-mp="1wnalcmpve92t">{shopLabels.rating(product)}</span>
          <span aria-hidden="true" data-id="element-8108" data-mp="2alqzgyq2b1uz">
            {rating(product.rating)} · {plural(product.reviews, 'beoordeling', 'beoordelingen')}
          </span>
        </a>}
      {product.note && <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-8109" data-mp="2p8pq4rh3hs8n">{product.note}</p>}

      {promo && <p className="mt-5 bg-blush px-4 py-3 text-[14px] leading-relaxed text-ink" data-id="element-8110" data-mp="2u7sndpqu2u62">
          <span className="font-medium" data-id="element-8111" data-mp="2u5g4nad94fry">{promo.label}</span>
          {promo.until && <> — {promo.until}</>}
        </p>}

      {/* Alleen een keuze tonen als er iets te kiezen is: zonder bijvullen is "Eenmalig" de enige optie. */}
      {options.length > 1 ? <div className="mt-7" data-id="element-8112" data-mp="26gn8bcspw5wt">
          <PurchaseOptions name="purchase" price={product.price} options={options} value={subscribe} onChange={onSubscribe} data-id="element-8113" data-mp="2b8y546s5vbl5" />
        </div> : <p className="mt-7 flex items-baseline justify-between gap-3 border-t border-line pt-4" data-id="element-8114" data-mp="1wld2edz77edr">
          <span className="text-[13px] text-muted" data-id="element-8115" data-mp="21jwr2m56f49t">{options[0]?.meta}</span>
          <span className="type-title text-ink" data-id="element-8116" data-mp="1niu509e6ltyw">{euro(product.price)}</span>
        </p>}
      {plan && !plan.ok && <p className="mt-3 text-[13px] leading-relaxed text-muted" data-id="element-8117" data-mp="1rjnrcybz70po">
          Bijvullen kan samen met een ander product: een doos is altijd minstens {euro(plan.minBoxValue)}, zodat verzending gratis blijft.
        </p>}

      {soldOut ? <div className="mt-5 space-y-2" data-id="element-8118" data-mp="1arxy65iwvrne">
          <p className="text-center text-[14px] text-muted" data-id="element-8119" data-mp="1aqwbt4wg3iqd">{shopLabels.soldOutShort}.</p>
          {/* Voorheen alleen tekst ("app Sarah"); nu een echte knop. */}
          <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${btnSecondary} w-full`} data-id="element-8120" data-mp="11qzrtgl0381n">
            <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8121" data-mp="wsg6nie8e0ac" />
            Vraag {founder.firstName} om een alternatief
            <span className="sr-only" data-id="element-8122" data-mp="rtlkq7kyvvm0">{shopLabels.viaWhatsApp}</span>
          </a>
        </div> : <button type="button" onClick={onAdd} className={`${btnPrice} mt-5`} data-id="element-8123" data-mp="mvtiajmp4r3k">
          <AnimatePresence mode="wait" initial={false} data-id="element-8124" data-mp="2m5tehhljy6g7">
            <motion.span key={added ? 'added' : 'add'} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.15,
          ease
        }} className={`flex w-full items-center gap-4 ${added ? 'justify-center' : 'justify-between'}`} data-id="element-8125" data-mp="2h89ndh3r9z6u">
              {added ? <span className="flex items-center gap-2" data-id="element-8126" data-mp="33fx5u309uikw">
                  <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-8127" data-mp="26qxzwun5tsvv" />
                  Toegevoegd
                </span> : <>
                  <span data-id="element-8128" data-mp="2fr4aa7nkacqt">In winkelmand</span>
                  <span className={btnPriceAmount} data-id="element-8129" data-mp="2fq2zm4agjs9m">{euro(product.price)}</span>
                </>}
            </motion.span>
          </AnimatePresence>
        </button>}

      {added && !soldOut ? <p className="mt-3 text-center text-[14px]" data-id="element-8130" data-mp="3r5rmywrmitay">
          <button type="button" onClick={onViewCart} aria-haspopup="dialog" className="min-h-[44px] text-ink underline underline-offset-4 hover:text-clay" data-id="element-8131" data-mp="3w4b9aya4q73u">
            Bekijk je winkelmand
          </button>
        </p> : giftHint && !soldOut && <p className="mt-3 text-center text-[13px] text-clay" data-id="element-8132" data-mp="31jynu7zlhf84">{giftHint}</p>}

      <ul className="mt-6 space-y-2.5 border-t border-line pt-5 text-[14px] text-muted" data-id="element-8133" data-mp="3ged789n2mqnb">
        <li className="flex items-center gap-2.5" data-id="element-8134" data-mp="3bgl2ug58pc28">
          <TruckIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8135" data-mp="2nq7k5g8jopkb" />
          {policyCopy.delivery}
        </li>
        <li className="flex items-center gap-2.5" data-id="element-8136" data-mp="2isxohiukbho1">
          <ShieldCheckIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8137" data-mp="2xg0mdqmsrsbw" />
          {policyCopy.returns}, ook geopend
        </li>
        {!soldOut && <li className="flex items-center gap-2.5" data-id="element-8138" data-mp="24zzn9fvfuvck">
            <MessageCircleIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8139" data-mp="24yohxbmchfjc" />
            <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-8140" data-mp="200d830w1v514">
              Vraag {founder.firstName} of dit bij je past
              <span className="sr-only" data-id="element-8141" data-mp="5kobzinub8bv">{shopLabels.viaWhatsApp}</span>
            </a>
          </li>}
      </ul>

      {children}
    </div>;
}